<template>
  <div class="login-page">
    <div class="login-container">
      <ForgotPassword v-if="forgotPassword" @close="forgotPassword = false" />
      <div v-else class="login-form">
        <div class="logo">
          <img src="@/assets/logo-n.svg" alt="Nvoko Logo" />
        </div>
        <h2>Sign In</h2>
        <form @submit.prevent="onLoginClick">
          <div class="inputs">
            <div class="form-group">
              <input
                v-model="email"
                id="email"
                type="email"
                class="text-input dark"
                placeholder="Email address"
              />
            </div>
            <div class="form-group">
              <input
                v-model="password"
                id="password"
                type="password"
                class="text-input dark"
                placeholder="Password"
              />
            </div>
          </div>
          <button
            type="submit"
            class="primary"
            :disabled="!enableLoginButton || isLoading"
          >
            {{ loginButtonText }}
          </button>
        </form>
        <span class="forgot-password" @click="forgotPassword = true">
          Forgot Password?
        </span>

        <div class="register-link">
          Don't have an account?
          <router-link
            :to="
              $route.query.invitation
                ? {
                    name: 'Signup',
                    query: { invitation: $route.query.invitation }
                  }
                : '/register'
            "
            class="accent-link"
            >Register</router-link
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ForgotPassword from "@/components/modals/ForgotPassword.vue";
import LOGIN from "@/api/mutations/LOGIN.gql";
import GET_DEFAULT_ARTIST from "@/api/queries/GET_DEFAULT_ARTIST.gql";
import { normalizeEmail } from "@/utils/normalizeEmail";

export default {
  name: "LoginPage",
  components: {
    ForgotPassword
  },
  data() {
    return {
      email: null,
      password: null,
      isLoading: false,
      forgotPassword: false
    };
  },
  computed: {
    user() {
      return this.$store.getters["account/getUser"];
    },
    enableLoginButton() {
      return this.email && this.password;
    },
    loginButtonText() {
      return this.isLoading ? "Loading..." : "Log In";
    }
  },
  methods: {
    async login() {
      const {
        data: { login }
      } = await this.$apollo.mutate({
        mutation: LOGIN,
        variables: {
          email: normalizeEmail(this.email),
          password: this.password.trim()
        }
      });

      this.$token = login.token;
      this.$store.commit("account/authSuccess", login);
    },
    async onLoginClick() {
      try {
        this.isLoading = true;
        await this.login();
        const {
          data: { artists }
        } = await this.$apollo.query({
          query: GET_DEFAULT_ARTIST,
          variables: {
            userId: this.user.id
          }
        });
        this.$store.commit("account/updateArtist", artists[0]);
        if (this.user.status === "unverified")
          this.$router.push({
            name: "SignupVerification",
            query: { invitation: this.$route.query.invitation }
          });
        else if (!this.user.onboarding_complete)
          this.$router.push({
            name: "Onboarding",
            query: { invitation: this.$route.query.invitation }
          });
        else if (this.$route.query.invitation) {
          this.$router.push({
            name: "Invite",
            params: { token: this.$route.query.invitation }
          });
        } else this.$router.push({ name: "Catalog" });
      } catch (e) {
        this.$errorService.handleApolloError(e, "login");
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "../styles/_mixins.scss";

.login-page {
  background: linear-gradient(
    to bottom right,
    rgba(0, 0, 0, 0.95),
    rgba(20, 20, 20, 0.9)
  );
  min-height: 100vh;
  width: 100%;
  position: relative;
  color: white;
  overflow-x: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("/path-to-your-texture.png") repeat;
    opacity: 0.03;
    pointer-events: none;
  }

  .nav {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 15%;
    padding-top: 30px;
    position: relative;
    z-index: 1;

    .logo {
      padding: 7px;
      justify-content: flex-start;
      align-items: flex-start;
      display: flex;
    }
  }

  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 120px);
    position: relative;
    z-index: 1;

    .login-form {
      background: linear-gradient(
        145deg,
        rgba(40, 40, 40, 0.6),
        rgba(20, 20, 20, 0.8)
      );
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border-radius: 24px;
      padding: 60px;
      width: 450px;
      max-width: 90%;
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3),
        0 1px 1px rgba(255, 255, 255, 0.05) inset;
      border: 1px solid rgba(255, 255, 255, 0.05);
      display: flex;
      flex-direction: column;
      align-items: center;

      .logo {
        margin-bottom: 32px;
        img {
          filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.1));
        }
      }

      h2 {
        color: $white;
        font-size: 28px;
        margin-bottom: 20px;
        font-weight: 600;
        letter-spacing: -0.02em;
        background: linear-gradient(to right, #ffffff, #e0e0e0);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      form {
        margin-top: 20px;
        margin-bottom: 8px;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .inputs {
          width: 100%;

          .form-group {
            margin-bottom: 24px;

            input {
              @include details-light();
              color: $white;
              width: 100%;
              background: rgba(0, 0, 0, 0.2);
              border: 1px solid rgba(255, 255, 255, 0.1);
              transition: all 0.3s ease;

              &:focus {
                border-color: rgba(255, 255, 255, 0.2);
                box-shadow: 0 0 15px rgba(255, 255, 255, 0.05);
              }
            }
          }
        }

        button {
          font-weight: 500;
          font-size: 16px;
          line-height: 24px;
          letter-spacing: -0.22px;
          padding: 12px 40px;
          margin-top: 10px;
          background: linear-gradient(135deg, $accent, darken($accent, 15%));
          border-radius: 30px;
          transition: all 0.3s ease;

          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          }

          &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
        }
      }

      .forgot-password {
        color: $accent;
        cursor: pointer;
        margin-top: 1rem;
        transition: all 0.2s ease;

        &:hover {
          color: lighten($accent, 10%);
          text-decoration: none;
          text-shadow: 0 0 10px rgba($accent, 0.3);
        }
      }

      .register-link {
        margin-top: 30px;
        text-align: center;
        color: rgba(255, 255, 255, 0.7);

        .accent-link {
          color: $accent;
          font-weight: 600;
          margin-left: 5px;
          transition: all 0.2s ease;

          &:hover {
            color: lighten($accent, 10%);
            text-decoration: none;
            text-shadow: 0 0 10px rgba($accent, 0.3);
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .login-page {
    .nav {
      padding: 0 5%;

      .logo {
        width: 30%;
      }
    }

    .login-container {
      .login-form {
        padding: 30px 20px;
      }
    }
  }
}
.login-page .login-container {
  min-height: 100vh;
  padding: 24px 16px;
}
.login-page .login-container .login-form {
  width: 405px;
  padding: 32px;
  max-width: 100%;
  border-radius: 16px;
}
.login-page .login-container .login-form .logo {
  margin-bottom: 20px;
}
.login-page .login-container .login-form form {
  margin-top: 12px;
}
.login-page .login-container .login-form .form-group {
  margin-bottom: 16px;
}
.login-page input::placeholder {
  color: #c8bdc2;
  opacity: 1;
}
@media (max-width: 480px) {
  .login-page .login-container .login-form {
    padding: 24px;
  }
}
</style>
